'use client';

import classNames from 'classnames';
import { ComponentProps, Suspense, useCallback, useMemo } from 'react';
import useAlgoliaConfigs from '@alltrails/context/hooks/useAlgoliaConfigs';
import AdaChatbot from '@alltrails/core/components/AdaChatbot';
import { useClientNavigationPushState } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import getNaturalLanguageSearchUrl from '@alltrails/search/utils/getNaturalLanguageSearchUrl';
import type { ColorsTheme } from '@alltrails/types/ColorsTheme';
import useAdaChatbot from '../../hooks/useAdaChatbot';
import useNaturalLanguageSearchExperiment from '../../hooks/useNaturalLanguageSearchExperiment';
import ConditionalRenderer from './ConditionalRender';
import Dropdowns from './Dropdowns';
import LogoLink from './LogoLink';
import MobileMenu from './MobileMenu';
import SearchBox, { SearchBoxProps } from './SearchBox';
import SkipToMainContentButton from './SkipToMainContentButton';
import styles from './styles/styles.module.scss';
import { HeaderBackground } from './types';
import UserCTA from './UserCTA';

// https://www.figma.com/design/uArl1Yn1JPudAlXAqYFGs5/Global-Nav-[Handoff]?node-id=63-5827&t=2hSsVfor4WO27vQn-0

type NavigationHeaderProps = {
  background?: HeaderBackground;
  bottomBorder?: boolean;
  className?: string;
  hideSearch?: boolean;
  logoOnly?: boolean;
  logoColorOverride?: ComponentProps<typeof LogoLink>['logoColorOverride'];
  logoVariantOverride?: ComponentProps<typeof LogoLink>['logoVariantOverride'];
  leftThemeOverride?: ColorsTheme;
  rightThemeOverride?: ColorsTheme;
  onInputBlur?: SearchBoxProps['onInputBlur'];
  onInputFocus?: SearchBoxProps['onInputFocus'];
  searchClearOnSelect?: SearchBoxProps['clearOnSelect'];
  searchGetCurrentLatLng?: SearchBoxProps['getCurrentLatLng'];
  searchOnAddressSelect?: SearchBoxProps['onAddressSelect'];
  searchOnNaturalLanguageResultSelect?: SearchBoxProps['onNaturalLanguageResultSelect'];
  searchOnResultSelect?: SearchBoxProps['onResultSelect'];
  searchRequestLoading?: SearchBoxProps['searchRequestLoading'];
  searchPreventNavigationOnSelect?: SearchBoxProps['navigateOnSelect'];
  searchValue?: SearchBoxProps['value'];
  tertiarySearchElement?: SearchBoxProps['tertiaryElement'];
};

const NavigationHeader = ({
  background = 'primary',
  bottomBorder,
  className,
  hideSearch,
  logoOnly,
  logoColorOverride,
  logoVariantOverride,
  leftThemeOverride,
  rightThemeOverride,
  onInputBlur,
  onInputFocus: onInputFocusProp,
  searchClearOnSelect,
  searchGetCurrentLatLng,
  searchOnAddressSelect,
  searchOnNaturalLanguageResultSelect,
  searchOnResultSelect,
  searchPreventNavigationOnSelect = false,
  searchValue,
  tertiarySearchElement,
  searchRequestLoading
}: NavigationHeaderProps) => {
  const pushState = useClientNavigationPushState();
  const algoliaConfigs = useAlgoliaConfigs();
  const languageRegionCode = useLanguageRegionCode();
  const { isAdaReady, config } = useAdaChatbot();

  const { isNaturalLanguageSearchEnabled, onInputFocus: onNaturalLanguageInputFocus } = useNaturalLanguageSearchExperiment();
  const handleNaturalLanguageResultSelect = useMemo<SearchBoxProps['onNaturalLanguageResultSelect']>(() => {
    if (!isNaturalLanguageSearchEnabled) {
      return undefined;
    }
    return searchOnNaturalLanguageResultSelect
      ? searchOnNaturalLanguageResultSelect
      : (query, clickPosition, selectionType, selectionMethod, searchOrigin) => {
          const url = getNaturalLanguageSearchUrl({ query, clickPosition, languageRegionCode, selectionType, selectionMethod, searchOrigin });
          pushState(url);
        };
  }, [isNaturalLanguageSearchEnabled, searchOnNaturalLanguageResultSelect, languageRegionCode, pushState]);

  const onInputFocus = useCallback(() => {
    onInputFocusProp?.();
    onNaturalLanguageInputFocus?.();
  }, [onInputFocusProp, onNaturalLanguageInputFocus]);

  return (
    <header
      className={classNames(
        styles.header,
        styles[background],
        {
          [styles.bottomBorder]: bottomBorder
        },
        className
      )}
    >
      <SkipToMainContentButton />
      <nav className={styles.nav}>
        <div className={styles.left} {...(leftThemeOverride ? { 'data-at-theme-colors': leftThemeOverride } : {})}>
          <LogoLink background={background} logoColorOverride={logoColorOverride} logoVariantOverride={logoVariantOverride} />
        </div>
        {algoliaConfigs && !hideSearch && !logoOnly ? (
          <SearchBox
            configs={algoliaConfigs}
            clearOnSelect={searchClearOnSelect}
            getCurrentLatLng={searchGetCurrentLatLng}
            navigateOnSelect={!searchPreventNavigationOnSelect}
            onAddressSelect={searchOnAddressSelect}
            onInputBlur={onInputBlur}
            onInputFocus={onInputFocus}
            onNaturalLanguageResultSelect={handleNaturalLanguageResultSelect}
            onResultSelect={searchOnResultSelect}
            searchRequestLoading={searchRequestLoading}
            tertiaryElement={tertiarySearchElement}
            value={searchValue}
          />
        ) : null}
        {!logoOnly && (
          <div className={styles.right} {...(rightThemeOverride ? { 'data-at-theme-colors': rightThemeOverride } : {})}>
            <Suspense fallback={null}>
              <ConditionalRenderer renderWhen="mobile">
                <MobileMenu />
              </ConditionalRenderer>
              <ConditionalRenderer renderWhen="desktop">
                <Dropdowns background={background} />
                <UserCTA background={background} />
              </ConditionalRenderer>
            </Suspense>
            {isAdaReady && <AdaChatbot config={config} experiment />}
          </div>
        )}
      </nav>
    </header>
  );
};
export default NavigationHeader;
